import React, {forwardRef, Suspense, useEffect} from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import CloseIcon from '@material-ui/icons/Close';
import makeStyles from '@material-ui/core/styles/makeStyles';
import IconButton from '@material-ui/core/IconButton';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import QuickLinkBackButton from 'Components/common/QuickLinks/QuickLinkBackButton';
import * as analytics from 'Utils/analytics';
import KioskMenuItemPropType from 'Utils/propTypes/KioskMenuItemPropType';
import {Desktop, useDesktopMediaQuery} from 'Utils/ResponsiveUtil';
import * as mapManager from 'Utils/explorer/mapManager';
import lodashGet from 'lodash/get';
import KioskSignConfigPropType from 'Utils/propTypes/KioskSignConfigPropType';

const useStyles = makeStyles(() => ({
    button: {
        padding: 0,
        top: -3,
    },
}));

const CardStack = props => {
    const {
        navItem,
        items,
        getCardComponent,
        ref,
        hideCard,
        selectedQuickLink,
        setQuickLink,
        navigationHistory,
        signConfig,
    } = props;
    const selectedMenuItem = items.find(i => i.id === navItem.menuItemId);
    const title = lodashGet(signConfig, navItem.labelPropPath ?? selectedMenuItem.labelPropPath);
    const card = getCardComponent(navItem?.card?.key);

    const isDesktop = useDesktopMediaQuery();

    const classes = useStyles();

    // navigationHistory will always have length >= 4 for this card
    const previousCard = navigationHistory[navigationHistory.length - 2]?.key;
    const showBackButton =
        navItem.card.showBackButton ||
        (navItem.card.key === 'directions' &&
            [
                'featureSummary',
                'featureDetailsCard',
                'eventDetails',
                'promotionsDetailsCard',
            ].includes(previousCard));

    const onClose = () => {
        hideCard();
        if (navItem.id === 'search') {
            analytics.sendSearchTermEvent('Close', props.searchQuery);
        } else if (navItem.id === 'featureSummary') {
            analytics.sendSearchEvent(analytics.ACTIONS.TOUCH, 'Close');
        } else if (navItem.menuItemId === 'events') {
            analytics.sendCampaignEventsEvent(analytics.ACTIONS.TOUCH, 'Close');
        } else if (navItem.menuItemId === 'tenants') {
            analytics.sendDirectoryEvent(analytics.ACTIONS.TOUCH, 'Close');
        }
    };

    const onBack = () => {
        props.navigateBack();
        if (navItem.id === 'search') {
            analytics.sendSearchTermEvent('Back', props.searchQuery);
        } else if (navItem.id === 'featureSummary') {
            analytics.sendSearchEvent(analytics.ACTIONS.TOUCH, 'Back');
        } else if (navItem.menuItemId === 'events') {
            analytics.sendCampaignEventsEvent(analytics.ACTIONS.TOUCH, 'Back');
        } else if (navItem.menuItemId === 'tenants') {
            analytics.sendDirectoryEvent(analytics.ACTIONS.TOUCH, 'Back');
        }
    };

    useEffect(
        () => {
            mapManager.getMap().resize();
        },
        [navItem?.card?.key]
    );

    if (!card) {
        return null;
    }

    return (
        <div
            className={classnames('card-stack-wrapper', {
                'card-compact': navItem.card.isCompact && !isDesktop,
                'no-pointer-events': navItem.menuItemId === 'search',
            })}
        >
            <div
                className={classnames(
                    'card-stack-container',
                    `nav-item-id-${navItem?.id || 'none'}`,
                    {
                        'events-list': navItem.menuItemId === 'events',
                    }
                )}
                ref={ref}
                onClick={e => {
                    if (e.target === e.currentTarget) onClose();
                    else e.stopPropagation();
                }}
            >
                <div className="card" data-testid={navItem?.card?.key}>
                    {!navItem.card.isTitleHidden && (
                        <h2
                            className={classnames('card-stack-title', {
                                'with-sub-heading': !!navItem.card.hasSubHeading,
                            })}
                            data-testid="card-stack-title"
                        >
                            {showBackButton && (
                                <div className="btn-icon-24 m-r-8">
                                    <IconButton
                                        color="inherit"
                                        classes={{root: classes.button}}
                                        onClick={onBack}
                                    >
                                        <ChevronLeftIcon />
                                    </IconButton>
                                </div>
                            )}
                            {!!selectedQuickLink && (
                                <QuickLinkBackButton selectQuickLink={setQuickLink} />
                            )}

                            <span>{title}</span>
                            {!navItem.card.isClosedButtonHidden && <CloseIcon onClick={onClose} />}
                        </h2>
                    )}
                    {navItem.card.key === 'searchCard' &&
                        !selectedQuickLink && (
                            <Desktop>
                                <h2 className="card-stack-title bottom-padding-zero">
                                    <span>{title}</span>
                                </h2>
                            </Desktop>
                        )}
                    <Suspense fallback={<div className="suspense-fallback" />}>{card}</Suspense>
                </div>
            </div>
        </div>
    );
};

CardStack.propTypes = {
    items: PropTypes.arrayOf(KioskMenuItemPropType),
    navItem: PropTypes.object.isRequired,
    hideCard: PropTypes.func.isRequired,
    ref: PropTypes.object,
    getCardComponent: PropTypes.func.isRequired,
    navigateBack: PropTypes.func,
    searchQuery: PropTypes.string,
    selectedQuickLink: PropTypes.object,
    setQuickLink: PropTypes.func.isRequired,
    navigationHistory: PropTypes.array,
    signConfig: KioskSignConfigPropType,
};

export default forwardRef((props, ref) => CardStack({...props, ref}));
